import type { ColumnDef } from "@tanstack/table-core";
import { CheckCircle2, ChevronRight, FileText } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Item } from "@/components/ui/item";
import {
  PURCHASE_REQUEST_STATUS_COLORS,
  purchaseRequestStatusLabel,
} from "@/constants/purchase-request";
import { DataTable } from "@/lib/data-table";
import { DataTableColumnHeader } from "@/lib/data-table-column-header";
import type { GroupedWorkItems, WorkItemRow } from "@/types";
import { formatDateAR } from "@/utils/date";

const columns: ColumnDef<WorkItemRow>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "sector_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sector" />
    ),
    cell: ({ row }) => row.original.sector_name ?? "-",
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const status = row.original.status;
      const color =
        PURCHASE_REQUEST_STATUS_COLORS[
          status as keyof typeof PURCHASE_REQUEST_STATUS_COLORS
        ] ?? "";
      return (
        <Badge className={color}>{purchaseRequestStatusLabel(status)}</Badge>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "needed_by_on",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fecha límite" />
    ),
    cell: ({ row }) => {
      const deadline = row.original.needed_by_on;
      return deadline ? formatDateAR(deadline) : "-";
    },
    enableSorting: false,
  },
  {
    id: "action",
    cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
    enableSorting: false,
  },
];

interface WorkItemGroupProps {
  group: GroupedWorkItems;
  type: "request" | "assignment";
  onRowClick: (row: WorkItemRow) => void;
}

function WorkItemGroup({ group, type, onRowClick }: WorkItemGroupProps) {
  const logoUrl = group.items[0]?.organization.logo_url;
  const label = type === "request" ? "Solicitaste" : "Te asignaron";
  const description = type === "request"
    ? "Solicitudes que vos creaste y están en proceso"
    : "Solicitudes que tenés que gestionar como comprador";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Avatar className="h-7 w-7 rounded-md">
            <AvatarImage
              src={logoUrl ?? undefined}
              alt={group.organization.name}
              className="object-cover"
            />
            <AvatarFallback className="rounded-md bg-muted text-xs">
              {group.organization.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {group.organization.name}
              </span>
              <span className="text-xs text-muted-foreground">
                · {label}
              </span>
            </div>
            <span className="text-xs text-muted-foreground/70">
              {description}
            </span>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">
          {group.items.length}
        </span>
      </div>
      <DataTable
        columns={columns}
        data={group.items}
        manualSorting={false}
        manualPagination={false}
        pagination={false}
        onRowClick={onRowClick}
      />
    </div>
  );
}

interface MyWorkItemsSectionProps {
  myRequests: GroupedWorkItems[];
  myAssignments: GroupedWorkItems[];
  userRoles: {
    has_requester_role: boolean;
    has_buyer_role: boolean;
  };
  onRowClick: (row: WorkItemRow) => void;
}

export function MyWorkItemsSection({
  myRequests,
  myAssignments,
  userRoles,
  onRowClick,
}: MyWorkItemsSectionProps) {
  const hasRequests = userRoles.has_requester_role && myRequests.length > 0;
  const hasAssignments = userRoles.has_buyer_role && myAssignments.length > 0;

  if (!hasRequests && !hasAssignments) {
    return null;
  }

  const totalItems = myRequests.reduce((acc, g) => acc + g.items.length, 0) +
    myAssignments.reduce((acc, g) => acc + g.items.length, 0);

  // Combine both types, keeping track of which is which
  const combinedItems: {
    group: GroupedWorkItems;
    type: "request" | "assignment";
  }[] = [];
  myRequests.forEach((g) => combinedItems.push({ group: g, type: "request" }));
  myAssignments.forEach((g) => combinedItems.push({ group: g, type: "assignment" }));

  return (
    <Item variant="outline" className="flex-col items-stretch">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Mis solicitudes</span>
        </div>
        <Badge variant="secondary" className="text-xs">
          {totalItems}
        </Badge>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Solicitudes de compra donde participás activamente, ya sea como quien las creó o como comprador asignado.
      </p>

      {combinedItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="mt-3 text-sm font-semibold">Todo al día</h3>
          <p className="mt-1 text-center text-xs text-muted-foreground">
            No tenés solicitudes ni asignaciones pendientes.
          </p>
        </div>
      ) : (
        <div className="space-y-6 mt-4">
          {combinedItems.map(({ group, type }) => (
            <WorkItemGroup
              key={`${group.organization.slug}-${type}`}
              group={group}
              type={type}
              onRowClick={onRowClick}
            />
          ))}
        </div>
      )}
    </Item>
  );
}
